import { APIConfigData } from 'core/config/APIConfigData';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { assetsInitialState } from 'core/store/reducers/assetsReducer/AssetsInitialState';
import {
	deleteAssetsAsync,
	getAssetsAsync,
	uploadAssetsAsync,
} from 'core/store/reducers/assetsReducer/AssetsSliceThunk';

const ASSETS_PAGE_SIZE = APIConfigData.DEFAULT_PAGE_SIZE;

/* eslint-disable no-param-reassign */
const updateAssetTypeState = (state, prop, value, type) => {
	if (!state[prop]) return;
	const assetType = type ?? state.assetLibraryModalType;
	if (Object.hasOwn(state[prop], assetType)) state[prop][assetType] = value;
};

const setAssets = (state, assetList, type) => {
	const assetType = type ?? state.assetLibraryModalType;
	// if (Object.hasOwn(state[prop], assetType)) state[prop][assetType] = value;
	const prevAssetsMap = state.assetsMap;
	const set = new Set();
	const nextAssetsList = assetList.map((asset) => {
		prevAssetsMap[asset.id] = asset;
		set.add(asset.id);
		return asset.id;
	});
	state.assets[assetType] = nextAssetsList.concat(
		(state.assets[assetType] ?? [])
			.slice(ASSETS_PAGE_SIZE + 1)
			.filter((assetId) => !set.has(assetId)),
	);

	set.clear();
	state.assetsMap = prevAssetsMap;
};

const appendAssets = (state, assetList, page, type) => {
	// if (!state[prop]) return;
	const assetType = type ?? state.assetLibraryModalType;
	// if (Object.hasOwn(state[prop], assetType)) state[prop][assetType] = value;
	const prevAssetsList = state.assets[assetType];
	// const prevAssetsSet = new Set(prevAssetsList);
	const prevAssetsMap = { ...state.assetsMap };
	const set = new Set();
	const nextAssetsList = assetList.map((asset) => {
		prevAssetsMap[asset.id] = asset;
		set.add(asset.id);
		return asset.id;
	});
	state.assets[assetType] = prevAssetsList
		.slice(0, (page - 1) * ASSETS_PAGE_SIZE)
		.concat(nextAssetsList)
		.concat(
			prevAssetsList.slice((page + 1) * ASSETS_PAGE_SIZE).filter((assetId) => !set.has(assetId)),
		);
	set.clear();
	state.assetsMap = prevAssetsMap;
};

const addNewAssets = (state, newAssetList, type) => {
	const assetType = type ?? state.assetLibraryModalType;
	const prevAssetsList = state.assets[assetType];
	const prevAssetsMap = { ...state.assetsMap };
	const nextAssetsList = newAssetList.map((asset) => {
		prevAssetsMap[asset.id] = asset;
		return asset.id;
	});
	state.assets[assetType] = nextAssetsList.concat(prevAssetsList);
	state.assetsMap = prevAssetsMap;
};

const updateAssets = (state, assetList) => {
	const prevAssetsMap = state.assetsMap;
	assetList.forEach((asset) => {
		prevAssetsMap[asset.id] = {
			...asset,
		};
	});
	state.assetsMap = prevAssetsMap;
};

const deleteAssets = (state, assetIdList, type) => {
	const assetType = type ?? state.assetLibraryModalType;
	const prevAssetsMap = { ...state.assetsMap };
	const assetIdSet = new Set(assetIdList);
	state.assets[assetType] = (state?.assets?.[assetType] ?? []).filter((assetId) => {
		if (assetIdSet.has(assetId)) {
			delete prevAssetsMap[assetId];
			return false;
		}
		return true;
	});
};

// const getAssetTypeState = (state, prop, type) => {
// 	if (!state[prop]) return undefined;
// 	const assetType = type ?? state.assetLibraryModalType;
// 	return state[prop]?.[assetType];
// };

export const assetLibraryReducers = {
	toggleAssetLibraryModal: (state, action) => {
		state.isAssetLibraryModalOpen = action.payload.modalOpen;
		state.assetLibraryModalDetails = action.payload.modalDetails;
	},
	toggleTemplateLibraryModal: (state, action) => {
		state.isTemplateModalOpen = action.payload.modalOpen;
		state.assetLibraryModalDetails = action.payload.modalDetails;
	},
	setAssetsWithType: (state, action) => {
		setAssets(state, action.payload.value, action.payload.type);
	},

	appendAssetsWithType: (state, action) => {
		appendAssets(state, action.payload.value, action.payload.type);
	},

	addNewAssetWithType: (state, action) => {
		addNewAssets(state, [action.payload.value], action.payload.type);
	},

	updateAssetWithType: (state, action) => {
		updateAssets(state, [action.payload.value], action.payload.type);
	},

	deleteAssetIdsWithType: (state, action) => {
		deleteAssets(state, action.payload.assetIds, action.payload.type);
	},

	setAssetsCurrentPage: (state, action) => {
		updateAssetTypeState(state, 'assetsCurrentPage', action.payload.value, action.payload.type);
	},
	setUploadedAsset: (state, action) => {
		updateAssetTypeState(state, 'uploadedAsset', action.payload.value, action.payload.type);
	},
	setIsTemplateAssetSelected: (state, action) => {
		state.isTemplateAssetSelected = action.payload;
	},
	setAssetLibraryModalType: (state, action) => {
		state.assetLibraryModalType = action.payload;
	},
	resetAssetState: (state) => {
		Object.keys(assetsInitialState).forEach((key) => {
			state[key] = assetsInitialState[key];
		});
	},
};

export const assetLibraryExtraReducers = (builder) => {
	builder.addCase(getAssetsAsync.pending, (state, action) => {
		updateAssetTypeState(state, 'assetsListApiStage', ApiStage.LOADING, action?.meta?.arg?.type);
	});
	builder.addCase(getAssetsAsync.fulfilled, (state, action) => {
		updateAssetTypeState(state, 'assetsListApiStage', ApiStage.SUCCESS, action.payload.assetType);
		updateAssetTypeState(
			state,
			'assetsCurrentPage',
			action.meta.arg.pageNumber,
			action.payload.assetType,
		);
		if (action.meta.arg.pageNumber === 1) {
			setAssets(state, action.payload.assets, action.payload.assetType);
		} else {
			appendAssets(
				state,
				action.payload.assets,
				action.meta.arg.pageNumber,
				action.payload.assetType,
			);
		}
		const { hasNext, nextTs } = action.payload;
		updateAssetTypeState(state, 'assetsHasNextPage', hasNext, action.payload.assetType);
		updateAssetTypeState(state, 'assetsNextTs', nextTs, action.payload.assetType);
	});
	builder.addCase(getAssetsAsync.rejected, (state, action) => {
		updateAssetTypeState(state, 'assetsListApiStage', ApiStage.ERROR, action?.meta?.arg?.type);
		sendToast(ToastType.ASSET_FETCH_ERROR);
	});
	builder.addCase(uploadAssetsAsync.pending, (state, action) => {
		updateAssetTypeState(
			state,
			'assetsUploadApiStage',
			ApiStage.LOADING,
			action?.meta?.arg?.data?.type,
		);
	});
	builder.addCase(uploadAssetsAsync.fulfilled, (state, action) => {
		// updateAssetTypeState(state, 'assets', action.payload.assets, action.payload.assetType);
		updateAssetTypeState(state, 'assetsUploadApiStage', ApiStage.SUCCESS, action.payload.assetType);
		addNewAssets(state, [action.payload.value], action.payload.type);
		window.dispatchEvent(new CustomEvent('assetsScrollToTop'));
	});
	builder.addCase(uploadAssetsAsync.rejected, (state, action) => {
		updateAssetTypeState(
			state,
			'assetsUploadApiStage',
			ApiStage.ERROR,
			action?.meta?.arg?.data?.type,
		);
		sendToast(ToastType.UPLOAD_ASSET_ERROR);
	});
	builder.addCase(deleteAssetsAsync.pending, (state, action) => {
		updateAssetTypeState(state, 'assetsDeleteApiStage', ApiStage.LOADING, action?.meta?.arg?.type);
	});
	builder.addCase(deleteAssetsAsync.fulfilled, (state, action) => {
		const { assetIds, type } = action.payload;
		updateAssetTypeState(state, 'assetsDeleteApiStage', ApiStage.SUCCESS, type);
		deleteAssets(state, assetIds, type);
	});
	builder.addCase(deleteAssetsAsync.rejected, (state, action) => {
		updateAssetTypeState(state, 'assetsDeleteApiStage', ApiStage.ERROR, action?.meta?.arg?.type);
		sendToast(ToastType.DELETE_ASSET_ERROR);
	});
};
